import React, { useEffect, useMemo, useRef, useState } from "react";

const InterpolationAnimation = ({ isActive }) => {
  const xs = [10, 20, 32, 42, 52, 68, 84, 96];
  const ys = [55, 55, 75, 40, 58, 35, 60, 50];

  const points = useMemo(() => xs.map((x, i) => ({ x, y: ys[i] })), []);
  const splinePts = points.slice(0, 4);   // P1..P4
  const smoothPts = points.slice(4);      // P5..P8

  // Catmull–Rom -> cubic Bézier for three smooth segments (P5→P6, P6→P7, P7→P8)
  const segPath = (P0, P1, P2, P3) => {
    const c1 = { x: P1.x + (P2.x - P0.x) / 6, y: P1.y + (P2.y - P0.y) / 6 };
    const c2 = { x: P2.x - (P3.x - P1.x) / 6, y: P2.y - (P3.y - P1.y) / 6 };
    return `M${P1.x} ${P1.y} C${c1.x} ${c1.y} ${c2.x} ${c2.y} ${P2.x} ${P2.y}`;
  };
  const smoothSegments = useMemo(() => {
    if (smoothPts.length !== 4) return [];
    const [P5, P6, P7, P8] = smoothPts;
    return [
      segPath(P5, P5, P6, P7),
      segPath(P5, P6, P7, P8),
      segPath(P6, P7, P8, P8),
    ];
  }, [smoothPts]);

  // --- animation timing ---
  const SPLINE_SEGMENTS = splinePts.length - 1; // 3
  const BRIDGE_STEP = SPLINE_SEGMENTS;          // 3
  const SMOOTH_SEG_COUNT = 3;                   // 3
  const HOLD_STEPS = 2;
  const TOTAL_STEPS = SPLINE_SEGMENTS + 1 + SMOOTH_SEG_COUNT + HOLD_STEPS; // 9

  const [step, setStep] = useState(0);
  useEffect(() => {
    if (!isActive) {
      setStep(0);
      return;
    }
    const id = setInterval(() => setStep((s) => (s + 1) % TOTAL_STEPS), 850);
    return () => clearInterval(id);
  }, [TOTAL_STEPS, isActive]);

  // Reset detection for fast clear
  const prevStepRef = useRef(0);
  const isReset = step < prevStepRef.current;      // sprung von 8 -> 0
  useEffect(() => { prevStepRef.current = step; }, [step]);

  // Sichtbarkeiten
  const visibleSplineSegs = Math.min(step, SPLINE_SEGMENTS);
  const showBridge = step >= BRIDGE_STEP + 1;
  const visibleSmoothSegs =
    step <= BRIDGE_STEP ? 0 : Math.min(step - (BRIDGE_STEP + 1), SMOOTH_SEG_COUNT);

  // path lengths for reveal
  const segRefs = [useRef(null), useRef(null), useRef(null)];
  const [segLens, setSegLens] = useState([1, 1, 1]);
  useEffect(() => {
    if (!isActive) return;
    setSegLens(
      segRefs.map((r) =>
        r.current && typeof r.current.getTotalLength === 'function'
          ? r.current.getTotalLength() || 1
          : 1
      )
    );
  }, [smoothSegments, isActive]);

  return (
    <div className="rounded-lg overflow-hidden">
      <svg
        viewBox="0 0 100 100"
        width="100%"
        height="100%"
        style={{ backgroundColor: "#9333ea" }} // optional #9333ea
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* (1) Spline P1..P4 */}
        {Array.from({ length: visibleSplineSegs }).map((_, i) => (
          <line
            key={`seg-${i}`}
            x1={splinePts[i].x}
            y1={splinePts[i].y}
            x2={splinePts[i + 1].x}
            y2={splinePts[i + 1].y}
            stroke="#f3f4f6"
            strokeWidth="4"
            strokeLinecap="round"
          />
        ))}

        {/* (2) Bridge P4->P5 */}
        {showBridge && (
          <line
            x1={points[3].x}
            y1={points[3].y}
            x2={points[4].x}
            y2={points[4].y}
            stroke="#f3f4f6"
            strokeWidth="4"
            strokeLinecap="round"
          />
        )}

        {/* (3) Smooth segments with quick clear on reset */}
        {smoothSegments.map((d, i) => {
          const visible = i < visibleSmoothSegs; // 0..3
          const len = segLens[i] || 1;
          return (
            <path
              key={`smooth-${i}`}
              ref={segRefs[i]}
              d={d}
              fill="none"
              stroke="#f3f4f6"
              strokeWidth="4"
              strokeLinecap="round"
              style={{
                strokeDasharray: len,
                strokeDashoffset: visible ? 0 : len,
                // faster clearing here
                transition: isReset
                  ? "stroke-dashoffset 0ms linear"      // faster clear
                  : "stroke-dashoffset 600ms ease-out",  // normal reveal
              }}
            />
          );
        })}

        {/* Points */}
        {points.map((p, idx) => (
          <circle key={idx} cx={p.x} cy={p.y} r="3" fill="#f3f4f6" />
        ))}
      </svg>
    </div>
  );
};

export default InterpolationAnimation;
